import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { Gift, Loader2, CheckCircle2 } from "lucide-react";
import { redeemGift, getGiftVoucher } from "@/lib/gift.functions";
import { useAuthUser } from "@/hooks/useAuthUser";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ReaderTrigger } from "@/components/InPageReader";
import { CountrySelect, PasswordInput } from "@/components/auth/AccountFields";

const STORE = "rt_pending_gift_code";

export const Route = createFileRoute("/redeem-gift")({
  validateSearch: z.object({ code: z.string().optional(), login: z.coerce.string().optional() }),
  head: () => ({
    meta: [
      { title: "Redeem Your Gift Membership | Recovery Trauma™" },
      { name: "description", content: "Someone has gifted you a Recovery Trauma™ membership. Enter your code — no payment needed." },
      { property: "og:title", content: "Someone has gifted you a Recovery Trauma™ Membership!" },
      { property: "og:description", content: "Activate your gifted membership in a few steps. No payment, no renewal." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RedeemGiftPage,
});

type Done = { months: number; expiresAt: string; startsLater: boolean };
const input = "w-full rounded-xl border border-gold/40 bg-background px-3 py-3 text-base";
const fmt = (d: string) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

function RedeemGiftPage() {
  const search = Route.useSearch();
  const user = useAuthUser();
  const redeem = useServerFn(redeemGift);
  const lookup = useServerFn(getGiftVoucher);
  const [code, setCode] = useState(search.code ?? "");
  const [months, setMonths] = useState<number | null>(null);
  const [mode, setMode] = useState<"signup" | "login">(search.login ? "login" : "signup");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("");
  const [confirm, setConfirm] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState<Done | null>(null);
  const tried = useRef(false);
  // True only when the code arrived on its own (email link or saved before
  // verification) — never while the member is typing it by hand.
  const autoActivate = useRef(true);

  // Restore a code kept from before email verification.
  useEffect(() => {
    if (!search.code) {
      try { const s = localStorage.getItem(STORE); if (s) { autoActivate.current = true; setCode(s); } } catch { /* noop */ }
    }
  }, [search.code]);

  // Show the gift length as soon as a valid code is typed.
  useEffect(() => {
    const c = code.trim();
    if (c.length < 8) { setMonths(null); return; }
    const t = setTimeout(() => {
      lookup({ data: { code: c } }).then((r) => setMonths(r.voucher && !r.voucher.redeemed ? r.voucher.plan_months : null)).catch(() => setMonths(null));
    }, 400);
    return () => clearTimeout(t);
  }, [code, lookup]);

  async function activate() {
    setBusy(true); setError(null);
    try {
      const r = await redeem({ data: { code: code.trim() } });
      if ("error" in r && r.error) setError(r.error);
      else if ("ok" in r) {
        try { localStorage.removeItem(STORE); } catch { /* noop */ }
        setDone(r);
        setTimeout(() => window.location.assign("/dashboard"), 6000);
      }
    } catch { setError("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  }

  // Signed in with a code (after login or the verification link) → activate automatically.
  useEffect(() => {
    if (user && autoActivate.current && code.trim().length >= 8 && !tried.current && !done) { tried.current = true; void activate(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, code]);

  function keep() { try { localStorage.setItem(STORE, code.trim()); } catch { /* noop */ } }

  async function submitAccount(e: React.FormEvent) {
    e.preventDefault();
    if (code.trim().length < 8) { setError("Please enter your gift code first."); return; }
    keep(); setBusy(true); setError(null);
    if (mode === "login") {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      setBusy(false);
      if (error) setError("Email or password is incorrect.");
      else autoActivate.current = true; // signed in with this code → activate once
      return; // auto-activation runs once signed in
    }
    if (!firstName.trim() || !lastName.trim()) { setBusy(false); setError("Please enter your first and last name."); return; }
    if (!country) { setBusy(false); setError("Please choose your country."); return; }
    if (password.length < 8) { setBusy(false); setError("Your password needs at least 8 characters."); return; }
    if (password !== confirm) { setBusy(false); setError("The two passwords don't match. Please type them again."); return; }
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(`/redeem-gift?code=${encodeURIComponent(code.trim())}`)}`,
        data: {
          first_name: firstName.trim(), last_name: lastName.trim(), display_name: `${firstName.trim()} ${lastName.trim()}`, country,
          referral_source: "Gift membership",
          age_confirmed_18: true, terms_accepted_at: new Date().toISOString(), terms_version: "2026-07-13",
        },
      },
    });
    setBusy(false);
    if (error) { setError(error.message); return; }
    if (!data.session) setSent(true);
  }

  return (
    <main className="mx-auto w-full max-w-md px-4 py-10">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gold-light/50">
        <Gift className="h-7 w-7 text-[var(--gold-deep)]" aria-hidden />
      </div>
      <h1 className="text-center font-display text-3xl font-semibold leading-tight">
        Someone has gifted you a Recovery Trauma™ Membership!
      </h1>
      <p className="mt-3 text-center text-sm text-muted-foreground">
        A gentle space for Healing Beyond Survival Mode™ is waiting for you. No payment details needed.
      </p>

      {done ? (
        <div className="mt-6 rounded-2xl border border-gold/40 bg-card p-6 text-center" role="status">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--gold-deep)]" aria-hidden />
          <p className="mt-2 font-display text-xl">Your {done.months}-month gift is active 🌿</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {done.startsLater ? "It's added after your current membership and runs" : "Your membership starts today and runs"} until {fmt(done.expiresAt)}. It will never renew or charge you.
          </p>
          <p className="mt-3 text-xs text-muted-foreground">Taking you to your dashboard…</p>
          <Button asChild size="lg" className="mt-4 w-full rounded-full"><a href="/dashboard">Go to my dashboard</a></Button>
        </div>
      ) : sent ? (
        <div className="mt-6 rounded-2xl border border-gold/40 bg-card p-6 text-center" role="status">
          <p className="font-display text-xl">Check your email 💌</p>
          <p className="mt-2 text-sm text-muted-foreground">
            We sent a link to <strong>{email}</strong>. Tap it and you'll come straight back here — your gift code is saved and your membership activates automatically.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          <div>
            <label className="block text-sm font-medium" htmlFor="gift-code">Gift voucher code</label>
            <input id="gift-code" value={code} onChange={(e) => { setCode(e.target.value); tried.current = false; autoActivate.current = false; }}
              placeholder="RT-GIFT-XXXX-XXXX" autoCapitalize="characters" maxLength={40}
              className={`${input} mt-1 text-center font-mono text-lg tracking-wider`} />
            {months ? <p className="mt-2 text-center text-sm font-medium text-[var(--gold-deep)]">✓ {months}-month gift membership</p> : null}
          </div>

          {user ? (
            <Button size="lg" className="w-full rounded-full" disabled={busy || code.trim().length < 8} onClick={() => { tried.current = true; void activate(); }}>
              {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden /> : null} Activate my gift
            </Button>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2" role="group" aria-label="Account">
                <button type="button" className="rt-choice rounded-full py-2 text-sm" aria-pressed={mode === "signup"} onClick={() => setMode("signup")}>Create Account</button>
                <button type="button" className="rt-choice rounded-full py-2 text-sm" aria-pressed={mode === "login"} onClick={() => setMode("login")}>Log In</button>
              </div>
              <form onSubmit={submitAccount} className="space-y-3">
                {mode === "signup" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input aria-label="First name" placeholder="First name *" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={input} required maxLength={80} autoComplete="given-name" />
                    <input aria-label="Last name" placeholder="Last name *" value={lastName} onChange={(e) => setLastName(e.target.value)} className={input} required maxLength={80} autoComplete="family-name" />
                  </div>
                ) : null}
                <input aria-label="Email" type="email" placeholder="Email address *" value={email} onChange={(e) => setEmail(e.target.value)} className={input} required autoComplete="email" />
                {mode === "signup" ? <CountrySelect id="gift-country" value={country} onChange={setCountry} className={input} /> : null}
                <PasswordInput id="gift-password" label="Password" placeholder="Password *" value={password} onChange={setPassword} className={input} minLength={mode === "signup" ? 8 : 1} autoComplete={mode === "signup" ? "new-password" : "current-password"} />
                {mode === "signup" ? (
                  <>
                    <PasswordInput id="gift-confirm" label="Confirm password" placeholder="Confirm password *" value={confirm} onChange={setConfirm} className={input} minLength={8} />
                    {confirm.length > 0 && confirm !== password ? (
                      <p className="text-xs text-destructive" role="alert">The two passwords don't match yet.</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">At least 8 characters. Type it twice so we know it's right.</p>
                    )}
                  </>
                ) : null}
                {mode === "signup" ? (
                  <div className="flex items-start gap-2 text-sm">
                    <input id="gift-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-4 w-4" required />
                    <label htmlFor="gift-agree">I'm 18 or older and agree to the</label>
                    <span className="sr-only">documents:</span>
                  </div>
                ) : null}
                {mode === "signup" ? (
                  <p className="-mt-2 ps-6 text-sm">
                    <ReaderTrigger to="/terms" className="underline">Terms &amp; Conditions</ReaderTrigger> and{" "}
                    <ReaderTrigger to="/privacy" className="underline">Privacy Policy</ReaderTrigger>
                  </p>
                ) : null}
                {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
                <Button type="submit" size="lg" className="w-full rounded-full" disabled={busy || code.trim().length < 8 || (mode === "signup" && (!agree || !country || password !== confirm))}>
                  {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden /> : null}
                  {mode === "signup" ? "Create account & activate gift" : "Log in & activate gift"}
                </Button>
              </form>
            </>
          )}
          {user && error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
          <ul className="space-y-1 rounded-xl bg-muted/40 p-4 text-xs text-muted-foreground">
            <li>✓ No plan to choose and no payment details</li>
            <li>✓ Membership starts the day you redeem</li>
            <li>✓ Never renews automatically · each code works once</li>
            <li>✓ Already a member? Just log in — your gift is added on</li>
          </ul>
        </div>
      )}
    </main>
  );
}
